import React, { useCallback, useEffect, useState } from "react"
import PropTypes from "prop-types"
import { Alert, Button, Flex, Spin } from "antd"
import { useTranslation } from "react-i18next"
import PowerTranzCheckout from "ui/PowerTranzCheckout"
import { clearAttemptRef, getPowerTranzErrorMessage, getSavedAttemptRef, isConfirmedAttemptState } from "helpers/powertranz"
import { usePowerTranzStatusPolling } from "hooks/usePowerTranzStatusPolling"
import { ATTEMPT_STATES, CONFIRMING_STATES } from "constants/powertranz"
import styles from "./WidgetCheckout.module.css"

// Pays the checkout's booking on PowerTranz's hosted page, in its own window so the guest stays in
// the widget (the guide's site can't frame it). The outcome comes only from the status endpoint,
// polled from the moment the window opens; a confirmed payment hands over to onPaid (the widget's
// success screen), a failed or expired one offers a new attempt.
const PowerTranzPayment = ({ bookingId, promoCodes, saveCardOffer, isDisabled, onPaid, onSessionChange }) => {
  const { t } = useTranslation()
  const [attemptRef, setAttemptRef] = useState(null)
  // The card was submitted (result message, or the window closed on a settling attempt).
  const [isAwaitingOutcome, setIsAwaitingOutcome] = useState(false)
  // Remounts the checkout for a new attempt after a failure or expiry.
  const [checkoutKey, setCheckoutKey] = useState(0)

  const { status: polledStatus, isRequestFailed, isTimedOut } = usePowerTranzStatusPolling(attemptRef ? { attempt_ref: attemptRef } : null)
  // After a retry the last answer is about the previous attempt.
  const status = polledStatus?.attemptRef === attemptRef ? polledStatus : null
  const state = status?.state
  const isConfirmed = isConfirmedAttemptState(state)
  const isFailed = state === ATTEMPT_STATES.FAILED
  const isExpired = isAwaitingOutcome && state === ATTEMPT_STATES.EXPIRED
  const isConfirming = !isConfirmed && !isFailed && !isExpired && (CONFIRMING_STATES.includes(state) || isAwaitingOutcome)

  useEffect(() => {
    if (isConfirmed || isFailed) clearAttemptRef(bookingId)
    if (isConfirmed) onPaid()
  }, [isConfirmed, isFailed, bookingId]) // eslint-disable-line react-hooks/exhaustive-deps

  // While the window is open the attempt is polled, so a payment finished there is seen even if the
  // window never reports back.
  const handleSessionChange = useCallback(
    (isLive) => {
      onSessionChange?.(isLive)
      if (isLive) setAttemptRef(getSavedAttemptRef(bookingId))
    },
    [bookingId, onSessionChange]
  )

  const handleResult = useCallback((resultAttemptRef) => {
    if (resultAttemptRef) setAttemptRef(resultAttemptRef)
    setIsAwaitingOutcome(true)
  }, [])

  const handleRetry = () => {
    clearAttemptRef(bookingId)
    setAttemptRef(null)
    setIsAwaitingOutcome(false)
    setCheckoutKey((key) => key + 1)
  }

  // The success screen takes over once onPaid runs.
  if (isConfirmed) {
    return (
      <Flex justify="center" className="p-m">
        <Spin />
      </Flex>
    )
  }

  if (isFailed || isExpired) {
    return (
      <Flex vertical gap={12}>
        <Alert type="error" showIcon message={isExpired ? t("powertranz.errors.expired") : getPowerTranzErrorMessage(t, status.failureReason)} />
        <Button type="primary" shape="round" size="large" block onClick={handleRetry}>
          {isExpired ? t("powertranz.checkout.start_again") : t("powertranz.checkout.try_again")}
        </Button>
      </Flex>
    )
  }

  // The charge may already have gone through: never offer to pay again from here.
  if (isConfirming || isTimedOut) {
    return (
      <Flex vertical align="center" gap={8} className="p-m">
        {!isTimedOut && <Spin />}
        <strong>{isTimedOut ? t("powertranz.result.still_confirming_title") : t("powertranz.result.confirming_title")}</strong>
        <span className={styles.hint}>
          {(isTimedOut && t("powertranz.result.still_confirming_text")) ||
            (isRequestFailed && t("powertranz.result.status_unreachable")) ||
            t("powertranz.result.confirming_text")}
        </span>
      </Flex>
    )
  }

  return (
    <PowerTranzCheckout
      key={checkoutKey}
      bookingId={bookingId}
      promoCodes={promoCodes}
      saveCardOffer={saveCardOffer}
      isDisabled={isDisabled}
      onSessionChange={handleSessionChange}
      onResult={handleResult}
      isStayingOnPage
    />
  )
}

PowerTranzPayment.propTypes = {
  bookingId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  promoCodes: PropTypes.arrayOf(PropTypes.string),
  // Present when the guest may save the card for the balance (the API's powertranz_saves_card).
  saveCardOffer: PropTypes.shape({ guideName: PropTypes.string, balanceAmount: PropTypes.string, dueDate: PropTypes.string }),
  isDisabled: PropTypes.bool,
  // Called once the API confirms the payment.
  onPaid: PropTypes.func.isRequired,
  // Called with true while the hosted page is open, false otherwise.
  onSessionChange: PropTypes.func
}

PowerTranzPayment.defaultProps = {
  promoCodes: [],
  saveCardOffer: null,
  isDisabled: false,
  onSessionChange: undefined
}

export default PowerTranzPayment
